import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Loader2, Save, User, Shield, ShieldCheck, ShieldOff, Mail,
  Building2, GraduationCap, FileText, CheckCircle2, Clock, AlertCircle,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { MFAEnrollDialog } from '@/components/MFAEnrollDialog';
import { useQuery } from '@tanstack/react-query';
import { ROLE_LABELS, AppRole } from '@/constants/roles';
import { format } from 'date-fns';
import { formatNoteText } from '@/utils/formatNoteText';


interface MyProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const getInitials = (name: string) =>
  (name || '').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';

const formatRole = (role?: string | null) =>
  !role ? 'Staff' : (ROLE_LABELS[role as AppRole] || role.replace(/_/g, ' '));

const dueBadge = (dueDate: string | null) => {
  if (!dueDate) return null;
  const diff = Math.ceil((new Date(dueDate).getTime() - Date.now()) / 86400000);
  if (diff < 0) return { label: 'Overdue', cls: 'bg-red-100 text-red-700 border-red-200' };
  if (diff <= 7) return { label: 'Due Soon', cls: 'bg-amber-100 text-amber-700 border-amber-200' };
  return { label: 'Upcoming', cls: 'bg-blue-100 text-blue-700 border-blue-200' };
};

// Pretty-print activity log details: prefer names over UUIDs, format known fields.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const titleCase = (k: string) => k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const formatActivityDetails = (details: any): string => {
  if (!details || typeof details !== 'object') return '';
  const parts: string[] = [];
  const name = details.client_name || details.target_name || details.user || details.name;
  if (name) parts.push(`Client: ${name}`);
  if (Array.isArray(details.updates) && details.updates.length) {
    parts.push(`Updated: ${details.updates.join(', ')}`);
  }
  if (details.new_status) parts.push(`Status: ${titleCase(String(details.new_status))}`);
  if (details.old_status && details.new_status && details.old_status !== details.new_status) {
    parts[parts.length - 1] = `Status: ${titleCase(String(details.old_status))} → ${titleCase(String(details.new_status))}`;
  }
  if (details.role) parts.push(`Role: ${titleCase(String(details.role))}`);
  if (details.reason) parts.push(`Reason: ${details.reason}`);
  if (details.note) parts.push(`Note: ${String(details.note).slice(0, 80)}`);

  // Skip ids and already-shown keys
  const skip = new Set([
    'client_name', 'target_name', 'user', 'name',
    'updates', 'new_status', 'old_status', 'role', 'reason', 'note',
    'timestamp',
  ]);
  Object.entries(details).forEach(([k, v]) => {
    if (skip.has(k)) return;
    if (k === 'id' || k.endsWith('_id')) return;
    if (typeof v === 'string' && UUID_RE.test(v)) return;
    if (v === null || v === undefined || v === '') return;
    if (typeof v === 'object') return;
    const val = String(v);
    if (val.length > 60) return;
    parts.push(`${titleCase(k)}: ${val}`);
  });
  return parts.slice(0, 4).join(' · ');
};

const formatNoteContent = (content: string): string => formatNoteText(content);






export function MyProfileDialog({ open, onOpenChange }: MyProfileDialogProps) {
  const { user, userRole } = useAuth();
  const userId = user?.id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [showEnrollDialog, setShowEnrollDialog] = useState(false);

  useEffect(() => {
    if (open && userId) {
      loadProfile();
      checkMfaStatus();
    }
  }, [open, userId]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const [{ data }, { data: authData }] = await Promise.all([
        supabase.from('profiles').select('full_name, phone').eq('id', userId!).single(),
        supabase.auth.getUser(),
      ]);
      setFullName(data?.full_name || '');
      setPhone(data?.phone || '');
      setEmail(authData?.user?.email || '');
    } finally {
      setLoading(false);
    }
  };

  const checkMfaStatus = async () => {
    try {
      const { data } = await supabase.auth.mfa.listFactors();
      setMfaEnabled(!!data?.totp.some(f => f.status === 'verified'));
    } catch {/* ignore */}
  };

  const handleDisableMfa = async () => {
    try {
      setMfaLoading(true);
      const { data } = await supabase.auth.mfa.listFactors();
      const verified = data?.totp.find(f => f.status === 'verified');
      if (!verified) return;
      const { error } = await supabase.auth.mfa.unenroll({ factorId: verified.id });
      if (error) throw error;
      setMfaEnabled(false);
      toast({ title: '2FA Disabled' });
    } catch (e: any) {
      toast({ title: 'Failed to disable 2FA', description: e.message, variant: 'destructive' });
    } finally {
      setMfaLoading(false);
    }
  };

  const handleSave = async () => {
    if (!fullName.trim()) {
      toast({ title: 'Name is required', variant: 'destructive' });
      return;
    }
    try {
      setSaving(true);
      const { error } = await supabase
        .from('profiles')
        .update({ full_name: fullName.trim(), phone: phone.trim() || null })
        .eq('id', userId!);
      if (error) throw error;
      toast({ title: 'Profile updated' });
    } catch {
      toast({ title: 'Failed to update profile', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // Assigned facilities
  const { data: sites = [] } = useQuery({
    queryKey: ['my-sites', userId],
    queryFn: async () => {
      if (!userId) return [];
      const { data } = await supabase
        .from('user_sites')
        .select('site_id, sites(id, name)')
        .eq('user_id', userId);
      return (data || []).map((r: any) => r.sites).filter(Boolean);
    },
    enabled: !!userId && open,
  });

  // Notes I created + notes/alerts I'm mentioned in
  const { data: createdNotes = [] } = useQuery({
    queryKey: ['my-created-notes', userId],
    queryFn: async () => {
      if (!userId) return [];
      const { data } = await supabase
        .from('notes')
        .select('id, title, content, note_type, created_at, clients(first_name, last_name)')
        .eq('created_by', userId)
        .is('file_url', null)
        .order('created_at', { ascending: false })
        .limit(15);
      return (data || []).map((n: any) => ({ ...n, source: 'created' as const }));
    },
    enabled: !!userId && open,
  });

  const { data: mentionedNotes = [] } = useQuery({
    queryKey: ['my-mentioned-notes', userId],
    queryFn: async () => {
      if (!userId) return [];
      const { data: mentions } = await supabase
        .from('mentions')
        .select('note_id')
        .eq('mentioned_user_id', userId)
        .not('note_id', 'is', null);
      const ids = (mentions || []).map((m: any) => m.note_id).filter(Boolean);
      if (ids.length === 0) return [];
      const { data } = await supabase
        .from('notes')
        .select('id, title, content, note_type, created_at, clients(first_name, last_name)')
        .in('id', ids)
        .is('file_url', null)
        .order('created_at', { ascending: false })
        .limit(15);
      return (data || []).map((n: any) => ({ ...n, source: 'mentioned' as const }));
    },
    enabled: !!userId && open,
  });

  const myNotes = [...createdNotes, ...mentionedNotes]
    .filter((n, i, arr) => arr.findIndex(x => x.id === n.id) === i)
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 20);

  // Trainings
  const { data: trainingAssignments = [] } = useQuery({
    queryKey: ['my-training-assignments', userId],
    queryFn: async () => {
      if (!userId) return [];
      const { data } = await supabase
        .from('training_assignments')
        .select('id, due_date, notes, training_materials(id, title, category)')
        .eq('assigned_to_user_id', userId)
        .order('due_date', { ascending: true });
      return data || [];
    },
    enabled: !!userId && open,
  });

  const { data: trainingCompletions = [] } = useQuery({
    queryKey: ['my-training-completions', userId],
    queryFn: async () => {
      if (!userId) return [];
      const { data } = await supabase
        .from('training_completions')
        .select('id, completed_at, material_id, training_materials(id, title)')
        .eq('user_id', userId)
        .order('completed_at', { ascending: false });
      return data || [];
    },
    enabled: !!userId && open,
  });

  const completedIds = new Set(trainingCompletions.map((c: any) => c.material_id));

  // Recent activity
  const { data: activity = [] } = useQuery({
    queryKey: ['my-activity', userId],
    queryFn: async () => {
      if (!userId) return [];
      const { data } = await supabase
        .from('user_activity_logs')
        .select('id, action_type, action_details, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(20);
      return data || [];
    },
    enabled: !!userId && open,
  });

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] p-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle className="flex items-center gap-2 text-xl">
              <User className="h-5 w-5" /> My Profile
            </DialogTitle>
          </DialogHeader>

          <ScrollArea className="h-[calc(90vh-80px)]">
            <div className="p-6 space-y-6">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  {/* Header */}
                  <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg">
                    <Avatar className="h-20 w-20 border-4 border-background shadow-lg">
                      <AvatarFallback className="text-2xl bg-primary text-primary-foreground">
                        {getInitials(fullName)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold">{fullName || 'Unnamed User'}</h3>
                      <p className="text-muted-foreground">{formatRole(userRole)}</p>
                      <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1.5"><Mail className="h-4 w-4" /><span>{email}</span></div>
                        <div className="flex items-center gap-1.5"><Building2 className="h-4 w-4" /><span>{sites.length} facilit{sites.length === 1 ? 'y' : 'ies'}</span></div>
                        <div className="flex items-center gap-1.5">
                          {mfaEnabled
                            ? <ShieldCheck className="h-4 w-4 text-emerald-600" />
                            : <ShieldOff className="h-4 w-4" />}
                          <span>2FA {mfaEnabled ? 'On' : 'Off'}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Tabs defaultValue="overview">
                    <TabsList className="grid w-full grid-cols-4">
                      <TabsTrigger value="overview">Overview</TabsTrigger>
                      <TabsTrigger value="notes">Notes & Alerts</TabsTrigger>
                      <TabsTrigger value="trainings">Trainings</TabsTrigger>
                      <TabsTrigger value="activity">Activity</TabsTrigger>
                    </TabsList>

                    {/* OVERVIEW */}
                    <TabsContent value="overview" className="space-y-4 mt-4">
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label htmlFor="fullName">Full Name</Label>
                          <Input id="fullName" value={fullName} onChange={e => setFullName(e.target.value)} maxLength={100} />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email</Label>
                          <Input id="email" value={email} disabled />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="phone">Phone</Label>
                          <Input id="phone" type="tel" value={phone} onChange={e => setPhone(e.target.value)} maxLength={20} placeholder="(555) 123-4567" />
                          <p className="text-xs text-muted-foreground">Used for internal communications only</p>
                        </div>
                      </div>

                      <Separator />

                      <div>
                        <Label className="flex items-center gap-2 mb-2"><Building2 className="h-4 w-4" /> Assigned Facilities</Label>
                        {sites.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No facilities assigned</p>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {sites.map((s: any) => (
                              <Badge key={s.id} variant="secondary">{s.name}</Badge>
                            ))}
                          </div>
                        )}
                      </div>

                      <Separator />

                      {/* MFA */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Shield className="h-4 w-4 text-muted-foreground" />
                            <Label>Two-Factor Authentication</Label>
                          </div>
                          {mfaEnabled
                            ? <Badge className="bg-emerald-600 hover:bg-emerald-600"><ShieldCheck className="h-3 w-3 mr-1" />Enabled</Badge>
                            : <Badge variant="secondary"><ShieldOff className="h-3 w-3 mr-1" />Disabled</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground">Add an extra layer of security with an authenticator app.</p>
                        {mfaEnabled ? (
                          <Button variant="outline" size="sm" onClick={handleDisableMfa} disabled={mfaLoading} className="text-destructive hover:text-destructive">
                            {mfaLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Disable 2FA
                          </Button>
                        ) : (
                          <Button variant="outline" size="sm" onClick={() => setShowEnrollDialog(true)}>
                            <Shield className="h-4 w-4 mr-2" /> Enable 2FA
                          </Button>
                        )}
                      </div>

                      <Separator />

                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
                        <Button onClick={handleSave} disabled={saving}>
                          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                          Save
                        </Button>
                      </div>
                    </TabsContent>

                    {/* NOTES & ALERTS */}
                    <TabsContent value="notes" className="space-y-2 mt-4">
                      {myNotes.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-8 text-center">No notes or mentions yet.</p>
                      ) : (
                        myNotes.map((n: any) => (
                          <div key={n.id} className="p-3 rounded-md border bg-background">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="font-medium text-sm truncate">{n.title || 'Untitled'}</p>
                                {n.clients && (
                                  <p className="text-xs text-muted-foreground">
                                    Client: {n.clients.first_name} {n.clients.last_name}
                                  </p>
                                )}
                                {n.content && (
                                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2 whitespace-pre-wrap">{formatNoteContent(n.content)}</p>
                                )}

                              </div>
                              <Badge variant={n.source === 'mentioned' ? 'default' : 'secondary'} className="shrink-0">
                                {n.source === 'mentioned' ? '@mentioned' : 'created'}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                              {format(new Date(n.created_at), 'MMM d, yyyy · h:mm a')}
                            </p>
                          </div>
                        ))
                      )}
                    </TabsContent>

                    {/* TRAININGS */}
                    <TabsContent value="trainings" className="space-y-4 mt-4">
                      <div>
                        <h4 className="text-sm font-semibold flex items-center gap-2 mb-2">
                          <GraduationCap className="h-4 w-4" /> Trainings Due
                        </h4>
                        {trainingAssignments.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No trainings assigned.</p>
                        ) : (
                          <div className="space-y-2">
                            {trainingAssignments.map((a: any) => {
                              const done = completedIds.has(a.training_materials?.id);
                              const badge = !done ? dueBadge(a.due_date) : null;
                              return (
                                <div key={a.id} className="flex items-center justify-between p-3 rounded-md border bg-background">
                                  <div className="min-w-0">
                                    <p className="font-medium text-sm truncate">{a.training_materials?.title || 'Untitled'}</p>
                                    {a.due_date && (
                                      <p className="text-xs text-muted-foreground">
                                        Due {format(new Date(a.due_date), 'MMM d, yyyy')}
                                      </p>
                                    )}
                                  </div>
                                  {done ? (
                                    <Badge className="bg-emerald-600 hover:bg-emerald-600"><CheckCircle2 className="h-3 w-3 mr-1" />Done</Badge>
                                  ) : badge ? (
                                    <Badge variant="outline" className={badge.cls}>{badge.label}</Badge>
                                  ) : (
                                    <Badge variant="outline">Open</Badge>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      <Separator />

                      <div>
                        <h4 className="text-sm font-semibold flex items-center gap-2 mb-2">
                          <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Completed ({trainingCompletions.length})
                        </h4>
                        {trainingCompletions.length === 0 ? (
                          <p className="text-sm text-muted-foreground">Nothing completed yet.</p>
                        ) : (
                          <div className="space-y-1.5">
                            {trainingCompletions.map((c: any) => (
                              <div key={c.id} className="flex items-center justify-between text-sm p-2 rounded border bg-background">
                                <span className="truncate">{c.training_materials?.title || 'Training'}</span>
                                <span className="text-xs text-muted-foreground shrink-0 ml-2">
                                  {format(new Date(c.completed_at), 'MMM d, yyyy')}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </TabsContent>

                    {/* ACTIVITY */}
                    <TabsContent value="activity" className="space-y-2 mt-4">
                      {activity.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-8 text-center">No recent activity.</p>
                      ) : (
                        activity.map((a: any) => (
                          <div key={a.id} className="flex items-start gap-3 p-3 rounded-md border bg-background">
                            <Clock className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium">{(a.action_type || '').replace(/_/g, ' ')}</p>
                              {(() => {
                                const summary = formatActivityDetails(a.action_details);
                                return summary ? (
                                  <p className="text-xs text-muted-foreground line-clamp-2">{summary}</p>
                                ) : null;
                              })()}

                              <p className="text-xs text-muted-foreground mt-0.5">
                                {format(new Date(a.created_at), 'MMM d, yyyy · h:mm a')}
                              </p>
                            </div>
                          </div>
                        ))
                      )}
                    </TabsContent>
                  </Tabs>
                </>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <MFAEnrollDialog
        open={showEnrollDialog}
        onOpenChange={setShowEnrollDialog}
        onEnrolled={() => { setMfaEnabled(true); setShowEnrollDialog(false); }}
      />
    </>
  );
}
